import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ModeStandbyOutlined, PersonOff, People } from "@mui/icons-material";

import { CardLayout, MTable } from "../v3";
import { PurchaseService } from "../../services";
import { SubcontractorTableColumns } from "../../pages/subcontractors/constants";
import { EXPORT_SUBCONTRACTOR } from "../../constants";
import { derivePdfColumns, getPrintableRows, useHasPermission } from "../../utils";
import { handlePdfDownload, Listing } from "../pdf";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";
import { formatExpenseAccountBalance } from "src/utils/formatters";

export const SubcontractorList = ({ companyId = null, totalCount = 0, setTotalCount = () => {}, reloadData }) => {
  const [subcontractors, setSubcontractors] = useState([]);
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const {
    filter: selectedFilter,
    setFilter: setSelectedFilter,
    searchText,
    setSearchText,
    sortModel,
    setSortModel,
    paginationModel: paginationState,
    setPaginationModel: setPaginationState,
  } = usePersistedTableState({
    tableId: "subcontractors",
    defaults: { filter: true, sortModel: [{ field: "updatedAt", sort: "desc" }] },
    filterValues: [true, false, null],
  });
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);

  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);

  const hasExportSubcontractor = useHasPermission(EXPORT_SUBCONTRACTOR);

  const fetchData = useCallback(
    async (subcontractorFilters = {}) => {
      setLoading(true);
      try {
        const { data: subcontractors } = await PurchaseService.getVendors({
          ...subcontractorFilters,
          type: "SUBCONTRACTOR",
          includeBalance: true,
          ...(companyId !== null && { companyId }),
        });
        const subcontractorsWithFormattedBalance = subcontractors?.rows?.map((subcontractor) => ({
          ...subcontractor,
          account: {
            ...subcontractor.account,
            formattedBalance: subcontractor.account?.balance ? formatExpenseAccountBalance(subcontractor.account.balance) : 0,
          },
        }));
        setSubcontractors(subcontractorsWithFormattedBalance);
        setTotalCount(subcontractors?.count);
        setLoading(false);
      } catch (error) {
        console.error(`🚀 ~ fetchData ~ error:`, error);
        setLoading(false);
      }
    },
    [setTotalCount, companyId],
  );

  useEffect(() => {
    fetchData({
      sortBy: sortModel[0]?.field,
      sortOrder: sortModel[0]?.sort,
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      active: selectedFilter,
      name: searchText,
    });
  }, [fetchData, sortModel, paginationState, selectedFilter, searchText, reloadData]);

  const handleFilterChange = (value) => {
    const filterValue = value === "null" ? null : JSON.parse(value);
    setSelectedFilter(filterValue);
  };

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfTitle = pdfTitleMap[String(selectedFilter)] || "All";
      const pdfColumnHeaders = derivePdfColumns(SubcontractorTableColumns);
      const printableRows = getPrintableRows(rows);

      const pdfDocument = (
        <Listing
          title={`Subcontractors : ${pdfTitle}`}
          columnHeaders={pdfColumnHeaders}
          tableRows={printableRows}
          company={user?.company}
        />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: `Subcontractors : ${pdfTitle}`,
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const onViewClick = (params) => {
    navigate(`/subcontractor/${params.row.id}`);
  };

  const filterOptions = [
    { label: "All", value: "null", icon: <ModeStandbyOutlined /> },
    { label: "Active", value: true, icon: <People /> },
    {
      label: "Inactive",
      value: false,
      icon: <PersonOff />,
    },
  ];

  const pdfTitleMap = {
    null: "All",
    true: "Active",
    false: "Inactive",
  };

  const rows = subcontractors
    ? subcontractors?.map((subcontractor, index) => ({
        sl: index + 1 + paginationState.page * paginationState.pageSize,
        id: subcontractor.id,
        name: subcontractor?.name,
        place: subcontractor?.primaryAddress?.locality,
        mobile: subcontractor?.primaryPhone,
        balance: subcontractor?.account?.balance,
        formattedBalance: subcontractor?.account?.formattedBalance,
      }))
    : [];
  return (
    <CardLayout>
      <MTable
        // Filter data
        selectedFilter={selectedFilter}
        filterOptions={filterOptions}
        onFilterChange={handleFilterChange}
        showFilterButton
        // Search data
        placeholder="Search subcontractor"
        searchText={searchText}
        onSearchChange={handleSearchChange}
        showSearchBar
        // Export data
        showExportButton={hasExportSubcontractor}
        isPdfGenerating={isPdfGenerating}
        onExportPdfClick={handleDownloadPdfClick}
        // Table data
        columnHeaders={SubcontractorTableColumns}
        tableRows={rows}
        rowCount={totalCount || 0}
        loading={loading}
        sortModel={sortModel}
        onSortChange={setSortModel}
        paginationModel={paginationState}
        onPaginationChange={handlePaginationChange}
        onRowClick={onViewClick}
      />
    </CardLayout>
  );
};
